import { TreemapPoint } from 'react-vis';
import orderBy from 'lodash/orderBy';
import {
    buyColourIndex,
    getAutoAssignedColourIndex,
    otherColourIndex,
    sellColourIndex,
    shortColourIndex,
} from 'utils/components/TreemapChart/treemapColours';
import { safeCalculatePercent } from 'utils/percent';

export interface TreemapNode {
    name: string;
    value: number;
}

export interface ReactVisNode<T extends TreemapNode> extends TreemapPoint {
    colourIndex: number;
    percent: number;
    /*
     * Note this value may differ from the source value for absolute values or to force the chart to render more sensibly for the small percentages threshold group
     * The original values can be found in the "data" nodes
     */
    value: number;
    /*
     * This will have one source node unless it is the "small percentages group" node
     */
    data: T[];
}

export function mapToReactVisData<T extends TreemapNode>(
    data: T[],
    percentGroupingThreshold: number,
    showOtherDrilldown: boolean,
    isSideSummary: boolean
): TreemapPoint {
    const nodes = data.map((n) => ({
        title: n.name,
        percent: 0,
        colourIndex: 0,
        data: [n],
        value: Math.abs(n.value),
    }));

    const total = sumValues(nodes);

    total &&
        nodes.forEach((c) => {
            c.percent = safeCalculatePercent(c.value, total);
        });

    const ordered = orderBy(nodes, ['percent'], ['desc']);

    const smallPercentagesGroupNode: ReactVisNode<T> = {
        title: 'Other',
        percent: 0,
        value: 0,
        colourIndex: 0,
        data: [],
    };

    const ret = ordered.reduce<ReactVisNode<T>[]>((nextNodes, node) => {
        if (node.percent >= percentGroupingThreshold) {
            nextNodes.push(node);
        } else {
            smallPercentagesGroupNode.percent += node.percent;
            smallPercentagesGroupNode.value += node.value;
            smallPercentagesGroupNode.data.push(node.data[0]);
        }
        return nextNodes;
    }, []);

    if (smallPercentagesGroupNode.percent > 0) {
        ret.push(smallPercentagesGroupNode);
        // Ensure that the small percentages group always takes percentGroupingThreshold% of the graph...
        if (smallPercentagesGroupNode.percent < percentGroupingThreshold) {
            smallPercentagesGroupNode.value =
                (sumValues(ret) * percentGroupingThreshold) / 100;
        }
    }

    smallPercentagesGroupNode.colourIndex = otherColourIndex;

    if (isSideSummary) {
        const buy = ret.find((r) => r.title === 'Buy');
        const sell = ret.find((r) => r.title === 'Sell');
        const short = ret.find((r) => r.title === 'Short');
        if (buy) {
            buy.colourIndex = buyColourIndex;
        }
        if (sell) {
            sell.colourIndex = sellColourIndex;
        }
        if (short) {
            short.colourIndex = shortColourIndex;
        }
    } else {
        const autoAssignedColourNodes = ret.filter(
            (r) => r !== smallPercentagesGroupNode
        );

        for (let i = 0; i < autoAssignedColourNodes.length; i++) {
            const nodeNeedingColour = autoAssignedColourNodes[i];
            nodeNeedingColour.colourIndex = getAutoAssignedColourIndex(i);
        }
    }

    if (showOtherDrilldown) {
        if (smallPercentagesGroupNode.percent > 0) {
            return mapToReactVisData(
                smallPercentagesGroupNode.data,
                0,
                showOtherDrilldown,
                isSideSummary
            );
        } else {
            // Drilling down. Ensure each node has a reasonable value...
            const minValue = total / 200;
            for (const tiny of nodes.filter((n) => n.percent < 1)) {
                tiny.value = Math.max(minValue, tiny.value);
            }
        }
    }

    return {
        title: '',
        children: ret,
    };
}

export function sumValues(nodes: { value?: number }[]): number {
    return nodes.reduce((value, child) => {
        return value + (child.value ?? 0);
    }, 0);
}
